/**
 * 登录后台后自动启动智能客服轮询（不必停留在「智能回复」页）
 * - 只连已开智能回复/AI 的店（懒连接）
 * - 若首启 shopN=0（账号未就绪 / 开关未读到）：自动重试，避免「客服不在线」空窗
 * - 登录会话内定时刷新，新开智能回复的店也会被拉上线
 */
import Api from "./api";
import Call from "./call";
import store from "@/store/index.js";
import {
    fetchDouDianAccounts,
    filterDouDianAuthorizedShops,
} from "./doudianLocalLogin";
import {
    filterDeskShops,
    loadDeskExcludedShopIds,
} from "./kefuDeskShops";

let inflight = null;
let retryTimer = null;
let refreshTimer = null;
let retryCount = 0;

/** 空窗重试：约 12s × 30 ≈ 6 分钟 */
const BOOT_RETRY_MS = 12000;
const BOOT_RETRY_MAX = 30;
/** 登录后周期性刷新，补上新开智能回复的店 / 纠正偶发空启 */
const BOOT_REFRESH_MS = 90 * 1000;

function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

function clearBootRetry() {
    if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }
}

function scheduleBootRetry(reason) {
    if (retryCount >= BOOT_RETRY_MAX) {
        try {
            console.warn(
                "[kefu] boot retry give up",
                reason,
                "count=",
                retryCount
            );
        } catch (e) {}
        return;
    }
    if (retryTimer) return;
    retryCount += 1;
    try {
        console.warn(
            "[kefu] boot retry scheduled",
            retryCount,
            "/",
            BOOT_RETRY_MAX,
            reason || ""
        );
    } catch (e) {}
    retryTimer = setTimeout(() => {
        retryTimer = null;
        ensureKefuSmartReplyPolling().catch(() => {});
    }, BOOT_RETRY_MS);
}

function ensureBootRefreshLoop() {
    if (refreshTimer) return;
    refreshTimer = setInterval(() => {
        ensureKefuSmartReplyPolling().catch(() => {});
    }, BOOT_REFRESH_MS);
}

function stopBootRefreshLoop() {
    if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
    }
}

async function fetchAuthorizedDeskShops() {
    const res = await fetchDouDianAccounts(Api.post.bind(Api), {
        requireLogin: true,
        status: 1,
        pageSize: 1000,
        source_type: 1,
    });
    let list = filterDouDianAuthorizedShops(
        (res && res.code == 1 && res.list) || []
    );
    list = filterDeskShops(list, loadDeskExcludedShopIds());
    return list;
}

async function doStart() {
    try {
        let list = await fetchAuthorizedDeskShops();
        // 刚进后台时账号列表偶发为空：短等再拉一次，减少 shopN=0 空窗
        if (!list.length) {
            await sleep(1200);
            list = await fetchAuthorizedDeskShops();
        }

        // 懒连接：默认只连已开智能回复/AI 的店
        let connectList = list;
        let smartOn = 0;
        let aiOn = 0;
        let flagReadOk = false;
        try {
            const [smartRs, aiRs] = await Promise.all([
                Call.invoke("kefuSmartReply.getSmartReplyEnabledBatch", list),
                Call.invoke("kefuSmartReply.getAiReplyEnabledBatch", list),
            ]);
            const smartMap =
                (smartRs && smartRs.code == 1 && smartRs.data) || {};
            const aiMap = (aiRs && aiRs.code == 1 && aiRs.data) || {};
            flagReadOk =
                !!(smartRs && smartRs.code == 1) &&
                !!(aiRs && aiRs.code == 1);
            if (smartRs && smartRs.code == 1 && smartRs.data) {
                store.commit(
                    "kefuSmartReply/SET_SMART_REPLY_ENABLED_MAP",
                    smartRs.data
                );
            }
            if (aiRs && aiRs.code == 1 && aiRs.data) {
                store.commit(
                    "kefuSmartReply/SET_AI_REPLY_ENABLED_MAP",
                    aiRs.data
                );
            }
            connectList = list.filter((s) => {
                const id = String(s.id);
                const smart = !!smartMap[id];
                const ai = !!aiMap[id];
                if (smart) smartOn += 1;
                if (ai) aiOn += 1;
                return smart || ai;
            });
        } catch (e) {
            // 开关读取失败时回退全量连接，保证自动回复不丢
            connectList = list;
            flagReadOk = false;
        }

        let maxConcurrentPolls = 24;
        let pollIntervalSec = 3;
        let smartIdleIntervalSec = 4;
        let reconcileIntervalSec = 45;
        try {
            const raw = localStorage.getItem("kefu.smartReply.speed");
            if (raw) {
                const o = JSON.parse(raw) || {};
                if (o.pollConcurrency >= 4 && o.pollConcurrency <= 64) {
                    maxConcurrentPolls = Number(o.pollConcurrency);
                }
                if (o.pollIntervalSec >= 1 && o.pollIntervalSec <= 15) {
                    pollIntervalSec = Number(o.pollIntervalSec);
                }
                if (o.smartIdleIntervalSec >= 1 && o.smartIdleIntervalSec <= 30) {
                    smartIdleIntervalSec = Number(o.smartIdleIntervalSec);
                }
                if (o.reconcileIntervalSec >= 15 && o.reconcileIntervalSec <= 180) {
                    reconcileIntervalSec = Number(o.reconcileIntervalSec);
                }
            } else {
                const n = Number(
                    localStorage.getItem("kefu.smartReply.maxConcurrentPolls")
                );
                if (n >= 4 && n <= 64) maxConcurrentPolls = n;
            }
        } catch (e) {}

        try {
            console.info(
                `[kefu] boot list=${list.length} connect=${connectList.length} smartOn=${smartOn} aiOn=${aiOn} flagsOk=${flagReadOk}`
            );
        } catch (e2) {}

        const rs = await Call.invoke("kefuSmartReply.start", connectList, {
            replySlaSec: 160,
            maxConcurrentPolls,
            pollIntervalSec,
            smartIdleIntervalSec,
            reconcileIntervalSec,
            // 不关掉已点选连上的闲置店
            retainOthers: true,
            lazyConnect: true,
        });
        if (rs && rs.code == 1 && rs.data) {
            store.commit("kefuSmartReply/APPLY_SNAPSHOT", rs.data);
            store.commit("kefuSmartReply/SET_STARTED", !!list.length);
        } else if (!list.length) {
            store.commit("kefuSmartReply/SET_STARTED", false);
        }

        // 空启必须重试：否则会出现「半小时 shopN=0 → 买家端客服不在线」
        if (!connectList.length) {
            const reason = !list.length
                ? "no-authorized-shops"
                : flagReadOk
                  ? "no-smart-ai-enabled"
                  : "smart-ai-flags-unread";
            scheduleBootRetry(reason);
        } else {
            retryCount = 0;
            clearBootRetry();
        }
        ensureBootRefreshLoop();
        return rs;
    } catch (e) {
        console.warn("[kefu] auto start polling failed", e);
        scheduleBootRetry("doStart-throw");
        ensureBootRefreshLoop();
        return { code: -1, msg: (e && e.message) || String(e) };
    }
}

/** 幂等：并发多次调用会合并；可重复调以刷新店铺列表 */
export function ensureKefuSmartReplyPolling() {
    if (inflight) return inflight;
    inflight = doStart().finally(() => {
        inflight = null;
    });
    return inflight;
}

export async function stopKefuSmartReplyPolling() {
    clearBootRetry();
    stopBootRefreshLoop();
    retryCount = 0;
    try {
        await Call.invoke("kefuSmartReply.stop");
    } catch (e) {}
    try {
        store.commit("kefuSmartReply/RESET");
    } catch (e) {}
}
